import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useAuth } from '../../lib/authContext';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import {
  AssetDetailV2,
  PassportPreviewCard,
  stripMfrPrefix,
  isAssetDetailTabId,
  type AssetDetailTabId,
} from '../../components/asset-v2/AssetDetailV2';
import { AssetComponentsCard, InstalledInNotice } from '../../components/asset-detail/AssetComponentsCard';
import { DriveSmartCard } from '../../components/asset-detail/DriveSmartCard';
import type {
  TelemetryScoreClientResponse,
  TelemetryPassportClientResponse,
} from '@siliconx/shared';
import { EnvironmentalSection } from '@siliconx/shared/ui/AssetPassportBody';
import { MaskedSerial } from '../../components/asset-detail/MaskedSerial';
import type { AssetDetail, DetailResponse, ApiError } from '../../components/asset-detail/asset-detail-types';
import {
  SummarySection,
  SkuSection,
  SpecsSection,
  ValuationHero,
  FmvHistoryChartCard,
  FmvHistoryTableCard,
  TelemetryChartCard,
  TelemetryTableCard,
  HealthScoreTrendCard,
} from '../../components/asset-detail/LegacySections';
import {
  SellerDetailsCard,
  LocationTeamNotesCard,
  LifecycleAdvanceButton,
  ListForSaleCard,
  AttachmentsSection,
  ProvenanceSection,
} from '../../components/asset-detail/EditableCards';
import { StockLineCard } from '../../components/asset-detail/StockLineCard';
import { UnitsCard } from '../../components/asset-detail/UnitsCard';
import { SerialEntryCard } from '../../components/asset-detail/SerialEntryCard';
import { PublicPassportCard } from '../../components/asset-detail/PublicPassportCard';

export const Route = createFileRoute('/assets/$assetId')({
  component: AssetDetailPage,
  // The at-a-glance redesign (AssetDetailV2) is the default; ?v=1 keeps the
  // legacy layout reachable until it is deleted.
  // `tab` selects the v2 group panel. It lives in the URL so a refresh, a
  // back-button, a bookmark, or a deep link from elsewhere in the product all
  // land on the same panel — an in-memory tab would silently drop the reader
  // back to "info" on every reload.
  validateSearch: (
    search: Record<string, unknown>,
  ): { v?: 1; tab?: AssetDetailTabId; listingVariant?: 'A' | 'B' | 'C' } => {
    const out: { v?: 1; tab?: AssetDetailTabId; listingVariant?: 'A' | 'B' | 'C' } = {};
    if (search.v === 1 || search.v === '1') out.v = 1;
    if (isAssetDetailTabId(search.tab)) out.tab = search.tab;
    // SIL-1170 PROTOTYPE — throwaway switcher for the "List for sale" seller-
    // fields exploration (ListingEditorSellerFieldsPrototype.tsx). Delete this
    // line along with that file once a variant is picked and folded in.
    if (search.listingVariant === 'A' || search.listingVariant === 'B' || search.listingVariant === 'C') {
      out.listingVariant = search.listingVariant;
    }
    return out;
  },
});

/**
 * Breadcrumb — lot members trail back through their lot to /lots.
 *
 * SIL-1036: data-driven on `asset.lotId`, not org type — an individual
 * customer_org's assets are never lot members (lots are `orgFeature:
 * 'lots'`, business-only), so this already renders no lot UI for one; see
 * `__tests__/breadcrumb.test.tsx`. Exported for that test.
 */
export function Breadcrumb({ asset }: { asset: AssetDetail }) {
  return (
    <div className="flex items-center gap-3">
      {asset.lotId != null ? (
        <>
          <BackLink as={Link} to="/lots">Lots</BackLink>
          <span className="text-muted-foreground">/</span>
          <Link
            to="/lots/$lotId"
            params={{ lotId: asset.lotId }}
            search={{ tab: 'overview' }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {asset.lotName ?? 'Lot'}
          </Link>
        </>
      ) : (
        <BackLink as={Link} to="/assets">Assets</BackLink>
      )}
      <span className="text-muted-foreground">/</span>
      <span className="text-sm font-medium text-foreground">
        {asset.assetTag ?? asset.id.slice(0, 8)}
      </span>
    </div>
  );
}

function AssetDetailPage() {
  const { assetId } = Route.useParams();
  const { v } = Route.useSearch();
  const { user } = useAuth();
  // RUF-448: only org admins can edit location/team/notes; a member without
  // asset-edit permission sees the read-only view (mirrors the admin gate on
  // PUT .../details and PUT /api/client/teams/asset/:assetId).
  const canEdit = user?.role === 'admin';

  const {
    data: resp,
    isLoading,
    error,
  } = useQuery<DetailResponse, ApiError>({
    queryKey: qk.assets.detail(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId'].$get({ param: { assetId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as DetailResponse;
    },
    retry: (failCount, err) => err.status !== 404 && failCount < 2,
  });

  // Telemetry health score (RUF-521) — lifted to the page so the top-of-page
  // ValuationHero and the relocated Health-score-trend card share one query.
  // 404 = not yet scored, treated as a quiet empty state (not an error).
  const { data: score = null } = useQuery<TelemetryScoreClientResponse | null, ApiError>({
    queryKey: qk.assets.telemetryScore(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId']['telemetry-score'].$get({
        param: { assetId },
      });
      if (res.status === 404) return null;
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as TelemetryScoreClientResponse;
    },
  });

  // Embodied carbon (SIL-190) — sourced from the passport payload so the
  // asset detail figure can never drift from the passport's. Same query key
  // as the passport page/embeds, so the cache is shared.
  const passportFingerprint = resp?.detail.fingerprint;
  const { data: passport = null } = useQuery<TelemetryPassportClientResponse | null, ApiError>({
    // SIL-1269: masked projection - distinct key so it never lands in the
    // (unmasked) cache the passport page and embeds share.
    queryKey: [...qk.assets.passport(passportFingerprint ?? ''), 'serials-masked'] as const,
    enabled: Boolean(passportFingerprint),
    queryFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].$get({
        param: { fingerprint: passportFingerprint! },
        query: { serials: 'masked' },
      });
      if (res.status === 404) return null;
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as TelemetryPassportClientResponse;
    },
  });

  if (isLoading) {
    return <LoadingShell rows={6} />;
  }

  const apiErr = error as ApiError | null;
  if (apiErr || !resp) {
    return (
      <div className="space-y-4">
        <BackLink as={Link} to="/assets">Assets</BackLink>
        <Card className="text-center text-muted-foreground">Asset not found.</Card>
      </div>
    );
  }

  const asset = resp.detail;
  const snapshotHistory = resp.snapshotHistory ?? [];
  const telemetryRecent = resp.telemetryRecent ?? [];
  const lifecycleEvents = resp.lifecycleEvents ?? [];
  const sellerMetadata = resp.sellerMetadata ?? null;

  const installedIn = asset.parentAssetId ? (
    <InstalledInNotice
      parentAssetId={asset.parentAssetId}
      parentLabel={asset.parentLabel}
      parentIsUnidentifiedChassis={asset.parentIsUnidentifiedChassis}
    />
  ) : null;
  // Boolean(), not `!== null`: an API that predates G9 sends undefined, and
  // that must read as "not a component" so nothing is hidden by mistake.
  const isComponent = Boolean(asset.parentAssetId);

  const assetTitle =
    asset.manufacturerName && asset.modelName
      ? `${asset.manufacturerName} ${stripMfrPrefix(asset.modelName, asset.manufacturerName)}`
      : (asset.assetTag ?? 'Asset detail');
  // SIL-1269: titles, QR labels and mailto bodies never carry the serial.
  const nonSerialLabel = asset.assetTag ?? asset.id.slice(0, 8);
  const assetSubtitle = (asset.quantity ?? 1) > 1 ? (
    `Qty ${asset.quantity.toLocaleString()}`
  ) : asset.hasSerial ? (
    <>
      Serial <MaskedSerial assetId={asset.id} hasSerial />
    </>
  ) : (
    'Asset detail'
  );

  if (v !== 1) {
    return (
      <div className="space-y-6">
        <Breadcrumb asset={asset} />
        <PageHeader
          title={assetTitle}
          subtitle={assetSubtitle}
          actions={
            <PassportPreviewCard
              fingerprint={asset.fingerprint}
              serialLabel={nonSerialLabel}
              serial={{ assetId: asset.id, hasSerial: asset.hasSerial }}
            />
          }
        />
        {installedIn}
        <StockLineCard
          assetId={asset.id}
          quantity={asset.quantity ?? 1}
          lineValueUsd={asset.lineValueUsd ?? null}
          includedLicenses={asset.includedLicenses ?? null}
          canEdit={canEdit}
        />
        <UnitsCard assetId={asset.id} quantity={asset.quantity ?? 1} canEdit={canEdit} />
        <SerialEntryCard
          assetId={asset.id}
          serialEditable={asset.serialEditable ?? false}
          serialEntered={Boolean(asset.serialEnteredAt)}
          canEdit={canEdit}
        />
        <PublicPassportCard
          assetId={asset.id}
          lifecycleStateId={asset.lifecycleStateId}
          isComponent={isComponent}
        />
        <DriveSmartCard assetId={asset.id} />
        <AssetDetailV2
          asset={asset}
          snapshotHistory={snapshotHistory}
          telemetryRecent={telemetryRecent}
          lifecycleEvents={lifecycleEvents}
          score={score}
          environmental={passport?.environmental ?? null}
          comparableFmv={resp.comparableFmv}
          components={
            asset.componentCount > 0 ? (
              <AssetComponentsCard parentAssetId={asset.id} canDetach={canEdit} />
            ) : undefined
          }
          provenance={<ProvenanceSection assetId={asset.id} />}
          sellerDetails={<SellerDetailsCard assetId={asset.id} initial={sellerMetadata} />}
          locationTeam={
            <LocationTeamNotesCard
              assetId={asset.id}
              dataCenterId={asset.dataCenterId}
              dataCenterName={asset.dataCenterName}
              teamId={asset.teamId}
              teamName={asset.teamName}
              notes={asset.notes}
              assetTag={asset.assetTag}
              canEdit={canEdit}
            />
          }
          attachments={<AttachmentsSection assetId={asset.id} />}
          lifecycleManagement={
            isComponent ? undefined : (
              <LifecycleAdvanceButton
                assetId={asset.id}
                lifecycleStateId={asset.lifecycleStateId}
                canEdit={canEdit}
              />
            )
          }
          listForSale={
            isComponent ? undefined : (
              <ListForSaleCard
                assetId={asset.id}
                allowsListing={asset.lifecycleAllowsListing}
                lifecycleStateId={asset.lifecycleStateId}
                fmvMedian={asset.latestFmv?.medUsd ?? null}
          fmvDirectional={asset.latestFmv?.isDirectionalBaseline === true}
                lotId={asset.lotId}
                lotName={asset.lotName}
              />
            )
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb asset={asset} />

      {/* Page title + digital passport entry link (RUF-555) */}
      <PageHeader
        title={assetTitle}
        subtitle={assetSubtitle}
        actions={
          <Link
            to="/passport/$fingerprint"
            params={{ fingerprint: asset.fingerprint }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            View digital passport &rarr;
          </Link>
        }
      />

      {/* Hero: valuation + telemetry health + List-for-sale CTA (RUF-521) —
          the top-of-page module mirroring the ops "Latest pricing" at-a-glance. */}
      <ValuationHero
        fmv={asset.latestFmv}
        fmvWithheldReason={asset.fmvWithheldReason ?? null}
        score={score}
        allowsListing={asset.lifecycleAllowsListing}
        inLot={asset.lotId != null}
      />

      {installedIn}
      <StockLineCard
        assetId={asset.id}
        quantity={asset.quantity ?? 1}
        lineValueUsd={asset.lineValueUsd ?? null}
        includedLicenses={asset.includedLicenses ?? null}
        canEdit={canEdit}
      />
      <UnitsCard assetId={asset.id} quantity={asset.quantity ?? 1} canEdit={canEdit} />
      <SerialEntryCard
        assetId={asset.id}
        serialEditable={asset.serialEditable ?? false}
        serialEntered={Boolean(asset.serialEnteredAt)}
        canEdit={canEdit}
      />
      <PublicPassportCard
        assetId={asset.id}
        lifecycleStateId={asset.lifecycleStateId}
        isComponent={isComponent}
      />

      {asset.componentCount > 0 && <AssetComponentsCard parentAssetId={asset.id} canDetach={canEdit} />}

      <DriveSmartCard assetId={asset.id} />

      {/* Section 1: Asset summary */}
      <SummarySection asset={asset} />

      {/* Section 1b: Ownership history / provenance — RUF-313 (hidden when none) */}
      <ProvenanceSection assetId={asset.id} />

      {/* Section 2: SKU / manufacturer (hidden when no SKU linked) */}
      <SkuSection asset={asset} />

      {/* Section 3: Specs (driven by classAttributes JSONB) */}
      <SpecsSection asset={asset} />

      {/* Section 3b: Environmental — embodied carbon (SIL-190), same figure
          as the digital passport's Environmental section. */}
      {passport && <EnvironmentalSection environmental={passport.environmental} />}

      {/* Sections 5-6: all trend graphs grouped together, each with a jump
          link to its data table below (FMV RUF-267, score RUF-518, telemetry
          RUF-268). */}
      <FmvHistoryChartCard history={snapshotHistory} />
      {score && (
        <HealthScoreTrendCard
          assetId={asset.id}
          categories={score.categories.map((c) => c.category)}
        />
      )}
      <TelemetryChartCard rows={telemetryRecent} />

      {/* Data tables backing the graphs above (scroll-contained, sticky headers). */}
      <FmvHistoryTableCard history={snapshotHistory} />
      <TelemetryTableCard rows={telemetryRecent} />

      {/* Section 7: Seller details — editable sidecar (RUF-279) */}
      <SellerDetailsCard assetId={asset.id} initial={sellerMetadata} />

      {/* Section 7b: Location + managing team + notes + asset tag (RUF-448, SIL-274) */}
      <LocationTeamNotesCard
        assetId={asset.id}
        dataCenterId={asset.dataCenterId}
        dataCenterName={asset.dataCenterName}
        teamId={asset.teamId}
        teamName={asset.teamName}
        notes={asset.notes}
        assetTag={asset.assetTag}
        canEdit={canEdit}
      />

      {/* Section 7c: Attachments — RUF-449 (certs/warranty docs) */}
      <AttachmentsSection assetId={asset.id} />

      {/* Section 8: List for sale — RUF-308 (only when lifecycle allows listing,
          and never on a component: it follows its server, spec section 4) */}
      {!isComponent && (
        <ListForSaleCard
          assetId={asset.id}
          allowsListing={asset.lifecycleAllowsListing}
          lifecycleStateId={asset.lifecycleStateId}
          fmvMedian={asset.latestFmv?.medUsd ?? null}
          fmvDirectional={asset.latestFmv?.isDirectionalBaseline === true}
          lotId={asset.lotId}
          lotName={asset.lotName}
        />
      )}

      {/* Section 9: Lifecycle management — RUF-280 (never on a component) */}
      {!isComponent && (
        <LifecycleAdvanceButton
          assetId={asset.id}
          lifecycleStateId={asset.lifecycleStateId}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
